import React from 'react';
import { Button, Flex, theme } from 'antd';
import { useTranslation } from 'react-i18next';

interface FormModalFooterProps {
  onCancel: () => void;
  /** Label of the primary button, e.g. "Create Resource" or "Save Changes". */
  submitLabel: React.ReactNode;
  /**
   * Called when the primary button is clicked. Leave it out when the footer sits inside a
   * `<Form>`: the button then submits the form and runs its `onFinish`.
   */
  onSubmit?: () => void;
  loading?: boolean;
  /** Disables the primary button, e.g. until a required choice is made. */
  submitDisabled?: boolean;
  /** Red primary button for destructive or irreversible actions such as refunds. */
  danger?: boolean;
  submitIcon?: React.ReactNode;
}

/**
 * Cancel / submit row shared by every modal that edits or creates something. Render it as the
 * last child of the modal body (inside the `<Form>` when there is one): it sticks to the bottom
 * of a scrolling body (see `FORM_MODAL_STYLES`) so the actions stay visible on long forms.
 */
export function FormModalFooter({
  onCancel,
  submitLabel,
  onSubmit,
  loading = false,
  submitDisabled = false,
  danger = false,
  submitIcon
}: FormModalFooterProps) {
  const { t } = useTranslation('common');
  const { token } = theme.useToken();

  return (
    <Flex
      justify="flex-end"
      wrap="wrap"
      gap={token.marginXS}
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        marginTop: token.marginMD,
        paddingTop: token.paddingSM,
        paddingBottom: token.paddingXXS,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgElevated
      }}
    >
      <Button onClick={onCancel} disabled={loading}>
        {t('common.cancel', { defaultValue: 'Cancel' })}
      </Button>
      <Button
        type="primary"
        danger={danger}
        icon={submitIcon}
        htmlType={onSubmit ? 'button' : 'submit'}
        onClick={onSubmit}
        loading={loading}
        disabled={submitDisabled}
      >
        {submitLabel}
      </Button>
    </Flex>
  );
}

export default FormModalFooter;
